import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { Award, Bell, ExternalLink } from 'lucide-react';
import { usePendingActions } from '../../context/PendingActionsContext';
import {
  trackCertificateNudgeBellCtaClick,
  trackCertificateNudgeBellOpen,
} from '../../lib/analytics';

const menuItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '13px 16px',
  background: 'transparent',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  cursor: 'pointer',
  textDecoration: 'none',
  color: 'inherit',
};

export function NotificationBell() {
  const { pendingCertificates, pendingCount } = usePendingActions();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next && pendingCount > 0) {
        trackCertificateNudgeBellOpen(pendingCount);
      }
      return next;
    });
  }, [pendingCount]);

  const handleCtaClick = useCallback(
    (projectId: number, certificateAccess: boolean, url: string) => {
      trackCertificateNudgeBellCtaClick(projectId, certificateAccess);
      setOpen(false);
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [],
  );

  if (pendingCount === 0) return null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={`Notifications, ${pendingCount} pending`}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid="notification-bell-button"
        onClick={handleToggle}
        style={{
          position: 'relative',
          width: 38,
          height: 38,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 8,
          color: 'rgba(248, 250, 252, 0.9)',
          cursor: 'pointer',
        }}
      >
        <Bell size={18} aria-hidden />
        <span
          aria-hidden
          data-testid="notification-bell-badge"
          style={{
            position: 'absolute',
            top: -4,
            right: -4,
            minWidth: 16,
            height: 16,
            padding: '0 4px',
            borderRadius: 999,
            background: '#ef4444',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid #101f3a',
          }}
        >
          {pendingCount}
        </span>
      </button>

      {open ? (
        <div
          role="menu"
          data-testid="notification-bell-dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 8,
            width: 320,
            background: '#0a1628',
            border: '1px solid var(--border)',
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden',
            zIndex: 200,
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#94a3b8',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            {pendingCount === 1 ? '1 thing needs your attention' : `${pendingCount} things need your attention`}
          </div>
          {pendingCertificates.map((action) => (
            <button
              key={action.projectId}
              type="button"
              role="menuitem"
              data-testid={`notification-action-${action.projectId}`}
              style={menuItemStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
              onClick={() =>
                handleCtaClick(action.projectId, action.certificateAccess, action.ctaUrl)
              }
            >
              <span
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'rgba(20, 184, 166, 0.16)',
                  color: 'var(--primary-light)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Award size={16} aria-hidden />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#f1f5f9',
                    marginBottom: 2,
                  }}
                >
                  {action.title}
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: 12.5,
                    color: '#94a3b8',
                    lineHeight: 1.4,
                  }}
                >
                  {action.description}
                </span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    marginTop: 6,
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: 'var(--primary-light)',
                  }}
                >
                  {action.ctaLabel}
                  <ExternalLink size={12} aria-hidden />
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default NotificationBell;
